import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { ClientRequirementsEditor } from "@/components/compliance/ClientRequirementsEditor";
import { DcEplChecklist } from "@/components/compliance/DcEplChecklist";
import { ExclusionScreeningPanel } from "@/components/compliance/ExclusionScreeningPanel";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EDIT_ROLES = ["system_administrator", "hr"];

export default function CompliancePage() {
  const { user } = useAuth();
  const location = useLocation();
  const canEdit = EDIT_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [exclusions, setExclusions] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(() => new URLSearchParams(location.search).get("client") || "");

  const load = useCallback(async () => {
    try {
      const [requirements, screening] = await Promise.all([api.get("/compliance/requirements"), api.get("/compliance/exclusions")]);
      setData(requirements.data);
      setExclusions(screening.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="app-shell" data-testid="compliance-shell">
      <AppSidebar activeItem="Client requirements" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page compliance-page" data-testid="compliance-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">CLIENT COMPLIANCE</p>
              <h1>Client requirements</h1>
              <p className="page-subtitle">Turn on the clearances each client requires, by provider role. Providers assigned to a client are checked against these requirements; gaps flag the assignment and open an HR task without blocking it.</p>
            </div>
          </section>
          {error && <p className="form-message error-message" data-testid="compliance-error">{error}</p>}
          {data && (
            <section className="compliance-card" data-testid="client-requirements-list">
              <div className="section-heading">
                <div>
                  <h2><Building2 size={17} /> Requirements by client</h2>
                  <p>All toggles start off. Medicaid enrollment defaults to related-service roles only and never applies to dedicated aides, support professionals or RBTs.</p>
                </div>
              </div>
              {data.clients.map((client) => (
                <article key={client.client_id} className={`compliance-client ${open === client.client_id ? "is-open" : ""}`} data-testid={`compliance-client-${client.client_id}`}>
                  <button type="button" className="compliance-client-toggle" data-testid={`compliance-client-toggle-${client.client_id}`} onClick={() => setOpen(open === client.client_id ? "" : client.client_id)}>
                    {open === client.client_id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <b>{client.client_name}</b>
                    <span data-testid={`compliance-client-summary-${client.client_id}`}>{client.enabled_labels.length ? client.enabled_labels.join(" · ") : "No requirements turned on"}</span>
                    {client.updated_at && <small>Updated {new Date(client.updated_at).toLocaleDateString()} by {client.updated_by_name}</small>}
                  </button>
                  {open === client.client_id && (
                    <ClientRequirementsEditor
                      key={`${client.client_id}-${client.updated_at || "new"}`}
                      canEdit={canEdit}
                      catalog={data.catalog}
                      client={client}
                      onSaved={(saved) => setData((d) => ({ ...d, clients: d.clients.map((c) => (c.client_id === saved.client_id ? { ...c, ...saved } : c)) }))}
                    />
                  )}
                </article>
              ))}
              {!data.clients.length && <p className="operations-empty-state">No active clients.</p>}
            </section>
          )}
          {exclusions && <ExclusionScreeningPanel canRun={canEdit} data={exclusions} onRefresh={load} />}
          {exclusions && <DcEplChecklist canEdit={canEdit} onChanged={load} />}
        </main>
      </div>
    </div>
  );
}
